/**
 * Component base class — powers all custom elements in the theme.
 *
 * Features:
 * - `ref="name"` system to reference child elements (auto-updated via MutationObserver)
 * - `ref="items[]"` for arrays of elements
 * - Declarative event delegation via `on:click="/methodName"` attributes
 * - `updatedCallback()` hook for Section Rendering API re-renders
 *
 * Based on Shopify Horizon's Component architecture.
 */

import { requestIdleCallback } from '@theme/utilities';

export class Component extends HTMLElement {
  /** @type {Record<string, Element | Element[] | undefined>} */
  refs = {};

  /** @type {string[] | undefined} */
  requiredRefs;

  connectedCallback() {
    registerEventListeners();
    this.#updateRefs();

    requestIdleCallback(() => {
      this.#mutationObserver.observe(this, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['ref'],
        attributeOldValue: true,
      });
    });
  }

  /** Called when the section is re-rendered via Section Rendering API */
  updatedCallback() {
    this.#mutationObserver.takeRecords();
    this.#updateRefs();
  }

  disconnectedCallback() {
    this.#mutationObserver.disconnect();
  }

  #updateRefs() {
    const refs = {};
    const elements = this.querySelectorAll('[ref]');

    for (const el of elements) {
      if (!this.#isDescendant(el)) continue;

      const refName = el.getAttribute('ref') ?? '';
      const isArray = refName.endsWith('[]');
      const path = isArray ? refName.slice(0, -2) : refName;

      if (isArray) {
        const array = Array.isArray(refs[path]) ? refs[path] : [];
        array.push(el);
        refs[path] = array;
      } else {
        refs[path] = el;
      }
    }

    if (this.requiredRefs?.length) {
      for (const ref of this.requiredRefs) {
        if (!(ref in refs)) {
          console.warn(`Required ref "${ref}" not found in <${this.tagName.toLowerCase()}>`);
        }
      }
    }

    this.refs = refs;
  }

  /** @type {MutationObserver} */
  #mutationObserver = new MutationObserver((mutations) => {
    if (
      mutations.some(
        (m) =>
          (m.type === 'attributes' && this.#isDescendant(m.target)) ||
          (m.type === 'childList' &&
            [...m.addedNodes, ...m.removedNodes].some((n) => this.#isDescendant(n)))
      )
    ) {
      this.#updateRefs();
    }
  });

  /** @param {Node} node */
  #isDescendant = (node) => getClosestComponent(node.parentNode) === this;
}

/**
 * Walk up the tree to find the closest Component ancestor.
 * @param {Node | null} node
 * @returns {HTMLElement | null}
 */
function getClosestComponent(node) {
  if (!node) return null;
  if (node instanceof Component) return node;
  if (node instanceof HTMLElement && node.tagName.toLowerCase().endsWith('-component')) return node;
  return node.parentNode ? getClosestComponent(node.parentNode) : null;
}

/**
 * Global event delegation system.
 * Listens for events on `[on:eventname]` attributes and routes them
 * to the method on the closest Component ancestor.
 *
 * Attribute format: `on:click="/methodName"` or `on:click="selector/methodName"`
 * With data: `on:click="/methodName?key=value"` or `on:click="/methodName/data"`
 */
let initialized = false;

function registerEventListeners() {
  if (initialized) return;
  initialized = true;

  const events = [
    'click',
    'change',
    'select',
    'focus',
    'blur',
    'submit',
    'input',
    'keydown',
    'keyup',
    'toggle',
  ];
  const shouldBubble = ['focus', 'blur'];
  const expensiveEvents = ['pointerenter', 'pointerleave'];

  for (const eventName of [...events, ...expensiveEvents]) {
    const attribute = `on:${eventName}`;

    document.addEventListener(
      eventName,
      (event) => {
        const element = getElement(event);
        if (!element) return;

        const proxiedEvent =
          event.target !== element
            ? new Proxy(event, {
              get(target, property) {
                if (property === 'target') return element;
                const value = Reflect.get(target, property);
                return typeof value === 'function' ? value.bind(target) : value;
              },
            })
            : event;

        const value = element.getAttribute(attribute) ?? '';
        let [selector, method] = value.split('/');

        const matches = value.match(/([\/\?][^\/\?]+)([\/\?][^\/\?]+)$/);
        const data = matches ? matches[2] : null;

        const instance = selector
          ? selector.startsWith('#')
            ? document.querySelector(selector)
            : element.closest(selector)
          : getClosestComponent(element);

        if (!(instance instanceof Component) || !method) return;

        method = method.replace(/\?.*/, '');

        const callback = /** @type {any} */ (instance)[method];

        if (typeof callback === 'function') {
          try {
            const args = [proxiedEvent];
            if (data) args.unshift(parseData(data));
            callback.call(instance, ...args);
          } catch (error) {
            console.error(error);
          }
        }
      },
      { capture: true }
    );
  }

  /** @param {Event} event */
  function getElement(event) {
    const target = event.composedPath?.()[0] ?? event.target;
    if (!(target instanceof Element)) return;

    if (target.hasAttribute(`on:${event.type}`)) return target;
    if (expensiveEvents.includes(event.type)) return null;

    return event.bubbles || shouldBubble.includes(event.type)
      ? target.closest(`[on\\:${event.type}]`)
      : null;
  }
}

/**
 * Parse data string from event attribute
 * @param {string} str
 */
function parseData(str) {
  const delimiter = str[0];
  const data = str.slice(1);

  return delimiter === '?'
    ? Object.fromEntries(
      Array.from(new URLSearchParams(data).entries()).map(([key, value]) => [key, parseValue(value)])
    )
    : parseValue(data);
}

/** @param {string} str */
function parseValue(str) {
  if (str === 'true') return true;
  if (str === 'false') return false;
  const num = Number(str);
  if (!isNaN(num) && str.trim() !== '') return num;
  return str;
}
